"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.BaseClient = void 0;

var _api = require("./api.js");

var _currentScopes = require("./currentScopes.js");

var _debugBuild = require("./debug-build.js");

var _envelope = require("./envelope.js");

var _integration = require("./integration.js");

var _session = require("./session.js");

var _dynamicSamplingContext = require("./tracing/dynamicSamplingContext.js");

var _clientreport = require("./utils-hoist/clientreport.js");

var _dsn = require("./utils-hoist/dsn.js");

var _envelope2 = require("./utils-hoist/envelope.js");

var _error = require("./utils-hoist/error.js");

var _is = require("./utils-hoist/is.js");

var _logger = require("./utils-hoist/logger.js");

var _misc = require("./utils-hoist/misc.js");

var _syncpromise = require("./utils-hoist/syncpromise.js");

var _parseSampleRate = require("./utils/parseSampleRate.js");

var _prepareEvent = require("./utils/prepareEvent.js");

var _spanUtils = require("./utils/spanUtils.js");

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i]; return arr2; }

function _iterableToArrayLimit(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

const ALREADY_SEEN_ERROR = "Not capturing exception because it's already been captured.";
/**
 * Base implementation for all JavaScript SDK clients.
 *
 * Call the constructor with the corresponding options
 * specific to the client subclass. To access these options later, use
 * {@link Client.getOptions}.
 *
 * If a Dsn is specified in the options, it will be parsed and stored. Use
 * {@link Client.getDsn} to retrieve the Dsn at any moment. In case the Dsn is
 * invalid, the constructor will throw a {@link SentryException}. Note that
 * without a valid Dsn, the SDK will not send any events to Sentry.
 *
 * Before sending an event, it is passed through
 * {@link BaseClient._prepareEvent} to add SDK information and scope data
 * (breadcrumbs and context). To add more custom information, override this
 * method and extend the resulting prepared event.
 *
 * To issue automatically created events (e.g. via instrumentation), use
 * {@link Client.captureEvent}. It will prepare the event and pass it through
 * the callback lifecycle. To issue auto-breadcrumbs, use
 * {@link Client.addBreadcrumb}.
 *
 * @example
 * class NodeClient extends BaseClient<NodeOptions> {
 *   public constructor(options: NodeOptions) {
 *     super(options);
 *   }
 *
 *   // ...
 * }
 */

class BaseClient {
  /** Options passed to the SDK. */

  /** The client Dsn, if specified in options. Without this Dsn, the SDK will be disabled. */

  /** Array of set up integrations. */

  /** Number of calls being processed */

  /** Holds flushable  */
  // eslint-disable-next-line @typescript-eslint/ban-types

  /**
   * Initializes this client instance.
   *
   * @param options Options for the client.
   */
  constructor(options) {
    this._options = options;
    this._integrations = {};
    this._numProcessing = 0;
    this._outcomes = {};
    this._hooks = {};
    this._eventProcessors = [];

    if (options.dsn) {
      this._dsn = (0, _dsn.makeDsn)(options.dsn);
    } else {
      _debugBuild.DEBUG_BUILD && _logger.logger.warn('No DSN provided, client will not send events.');
    }

    if (this._dsn) {
      const url = (0, _api.getEnvelopeEndpointWithUrlEncodedAuth)(this._dsn, options.tunnel, options._metadata ? options._metadata.sdk : undefined);
      this._transport = options.transport(_objectSpread(_objectSpread({
        tunnel: this._options.tunnel,
        recordDroppedEvent: this.recordDroppedEvent.bind(this)
      }, options.transportOptions), {}, {
        url
      }));
    } // TODO(v9): Remove this deprecation warning


    const tracingOptions = ['enableTracing', 'tracesSampleRate', 'tracesSampler'];
    const undefinedOption = tracingOptions.find(option => option in options && options[option] == undefined);

    if (undefinedOption) {
      (0, _logger.consoleSandbox)(() => {
        // eslint-disable-next-line no-console
        console.warn(`[Sentry] Deprecation warning: \`${undefinedOption}\` is set to undefined, which leads to tracing being enabled. In v9, a value of \`undefined\` will result in tracing being disabled.`);
      });
    }
  }
  /**
   * @inheritDoc
   */


  captureException(exception, hint, scope) {
    const eventId = (0, _misc.uuid4)(); // ensure we haven't captured this very object before

    if ((0, _misc.checkOrSetAlreadyCaught)(exception)) {
      _debugBuild.DEBUG_BUILD && _logger.logger.log(ALREADY_SEEN_ERROR);
      return eventId;
    }

    const hintWithEventId = _objectSpread({
      event_id: eventId
    }, hint);

    this._process(this.eventFromException(exception, hintWithEventId).then(event => this._captureEvent(event, hintWithEventId, scope)));

    return hintWithEventId.event_id;
  }
  /**
   * @inheritDoc
   */


  captureMessage(message, level, hint, currentScope) {
    const hintWithEventId = _objectSpread({
      event_id: (0, _misc.uuid4)()
    }, hint);

    const eventMessage = (0, _is.isParameterizedString)(message) ? message : String(message);
    const promisedEvent = (0, _is.isPrimitive)(message) ? this.eventFromMessage(eventMessage, level, hintWithEventId) : this.eventFromException(message, hintWithEventId);

    this._process(promisedEvent.then(event => this._captureEvent(event, hintWithEventId, currentScope)));

    return hintWithEventId.event_id;
  }
  /**
   * @inheritDoc
   */


  captureEvent(event, hint, currentScope) {
    const eventId = (0, _misc.uuid4)(); // ensure we haven't captured this very object before

    if (hint && hint.originalException && (0, _misc.checkOrSetAlreadyCaught)(hint.originalException)) {
      _debugBuild.DEBUG_BUILD && _logger.logger.log(ALREADY_SEEN_ERROR);
      return eventId;
    }

    const hintWithEventId = _objectSpread({
      event_id: eventId
    }, hint);

    const sdkProcessingMetadata = event.sdkProcessingMetadata || {};
    const capturedSpanScope = sdkProcessingMetadata.capturedSpanScope;

    this._process(this._captureEvent(event, hintWithEventId, capturedSpanScope || currentScope));

    return hintWithEventId.event_id;
  }
  /**
   * @inheritDoc
   */


  captureSession(session) {
    if (!(typeof session.release === 'string')) {
      _debugBuild.DEBUG_BUILD && _logger.logger.warn('Discarded session because of missing or non-string release');
    } else {
      this.sendSession(session); // After sending, we set init false to indicate it's not the first occurrence

      (0, _session.updateSession)(session, {
        init: false
      });
    }
  }
  /**
   * @inheritDoc
   */


  getDsn() {
    return this._dsn;
  }
  /**
   * @inheritDoc
   */


  getOptions() {
    return this._options;
  }
  /**
   * @see SdkMetadata
   *
   * @return The metadata of the SDK
   */


  getSdkMetadata() {
    return this._options._metadata;
  }
  /**
   * @inheritDoc
   */


  getTransport() {
    return this._transport;
  }
  /**
   * @inheritDoc
   */


  flush(timeout) {
    const transport = this._transport;

    if (transport) {
      this.emit('flush');
      return this._isClientDoneProcessing(timeout).then(clientFinished => {
        return transport.flush(timeout).then(transportFlushed => clientFinished && transportFlushed);
      });
    } else {
      return (0, _syncpromise.resolvedSyncPromise)(true);
    }
  }
  /**
   * @inheritDoc
   */


  close(timeout) {
    return this.flush(timeout).then(result => {
      this.getOptions().enabled = false;
      this.emit('close');
      return result;
    });
  }
  /** Get all installed event processors. */


  getEventProcessors() {
    return this._eventProcessors;
  }
  /** @inheritDoc */


  addEventProcessor(eventProcessor) {
    this._eventProcessors.push(eventProcessor);
  }
  /** @inheritdoc */


  init() {
    if (this._isEnabled() || // Force integrations to be setup even if no DSN was set when we have
    // Spotlight enabled. This is particularly important for browser as we
    // don't support the `spotlight` option there and rely on the users
    // adding the `spotlightBrowserIntegration()` to their integrations which
    // wouldn't get initialized with the check below when there's no DSN set.
    this._options.integrations.some(({
      name
    }) => name.startsWith('Spotlight'))) {
      this._setupIntegrations();
    }
  }
  /**
   * Gets an installed integration by its name.
   *
   * @returns The installed integration or `undefined` if no integration with that `name` was installed.
   */


  getIntegrationByName(integrationName) {
    return this._integrations[integrationName];
  }
  /**
   * @inheritDoc
   */


  addIntegration(integration) {
    const isAlreadyInstalled = this._integrations[integration.name]; // This hook takes care of only installing if not already installed

    (0, _integration.setupIntegration)(this, integration, this._integrations); // Here we need to check manually to make sure to not run this multiple times

    if (!isAlreadyInstalled) {
      (0, _integration.afterSetupIntegrations)(this, [integration]);
    }
  }
  /**
   * @inheritDoc
   */


  sendEvent(event, hint = {}) {
    this.emit('beforeSendEvent', event, hint);
    let env = (0, _envelope.createEventEnvelope)(event, this._dsn, this._options._metadata, this._options.tunnel);

    for (const attachment of hint.attachments || []) {
      env = (0, _envelope2.addItemToEnvelope)(env, (0, _envelope2.createAttachmentEnvelopeItem)(attachment));
    }

    const promise = this.sendEnvelope(env);

    if (promise) {
      promise.then(sendResponse => this.emit('afterSendEvent', event, sendResponse), null);
    }
  }
  /**
   * @inheritDoc
   */


  sendSession(session) {
    const env = (0, _envelope.createSessionEnvelope)(session, this._dsn, this._options._metadata, this._options.tunnel); // sendEnvelope should not throw
    // eslint-disable-next-line @typescript-eslint/no-floating-promises

    this.sendEnvelope(env);
  }
  /**
   * @inheritDoc
   */


  recordDroppedEvent(reason, category, eventOrCount) {
    if (this._options.sendClientReports) {
      // TODO v9: We do not need the `event` passed as third argument anymore, and can possibly remove this overload
      // If event is passed as third argument, we assume this is a count of 1
      const count = typeof eventOrCount === 'number' ? eventOrCount : 1; // We want to track each category (error, transaction, session, replay_event) separately
      // but still keep the distinction between different type of outcomes.
      // We could use nested maps, but it's much easier to read and type this way.
      // A correct type for map-based implementation if we want to go that route
      // would be `Partial<Record<SentryRequestType, Partial<Record<Outcome, number>>>>`
      // With typescript 4.1 we could even use template literal types

      const key = `${reason}:${category}`;
      _debugBuild.DEBUG_BUILD && _logger.logger.log(`Recording outcome: "${key}"${count > 1 ? ` (${count} times)` : ''}`);
      this._outcomes[key] = (this._outcomes[key] || 0) + count;
    }
  } // Keep on() & emit() signatures in sync with types' client.ts interface

  /* eslint-disable @typescript-eslint/unified-signatures */

  /** @inheritdoc */

  /** @inheritdoc */


  on(hook, callback) {
    const hooks = this._hooks[hook] = this._hooks[hook] || []; // @ts-expect-error We assume the types are correct

    hooks.push(callback); // This function returns a callback execution handler that, when invoked,
    // deregisters a callback. This is crucial for managing instances where callbacks
    // need to be unregistered to prevent self-referencing in callback closures,
    // ensuring proper garbage collection.

    return () => {
      // @ts-expect-error We assume the types are correct
      const cbIndex = hooks.indexOf(callback);

      if (cbIndex > -1) {
        hooks.splice(cbIndex, 1);
      }
    };
  }
  /** @inheritdoc */

  /** @inheritdoc */


  emit(hook, ...rest) {
    const callbacks = this._hooks[hook];

    if (callbacks) {
      callbacks.forEach(callback => callback(...rest));
    }
  }
  /**
   * @inheritdoc
   */


  sendEnvelope(envelope) {
    this.emit('beforeEnvelope', envelope);

    if (this._isEnabled() && this._transport) {
      return this._transport.send(envelope).then(null, reason => {
        _debugBuild.DEBUG_BUILD && _logger.logger.error('Error while sending envelope:', reason);
        return reason;
      });
    }

    _debugBuild.DEBUG_BUILD && _logger.logger.error('Transport disabled');
    return (0, _syncpromise.resolvedSyncPromise)({});
  }
  /* eslint-enable @typescript-eslint/unified-signatures */

  /** Setup integrations for this client. */


  _setupIntegrations() {
    const integrations = this._options.integrations;
    this._integrations = (0, _integration.setupIntegrations)(this, integrations);
    (0, _integration.afterSetupIntegrations)(this, integrations);
  }
  /** Updates existing session based on the provided event */


  _updateSessionFromEvent(session, event) {
    let crashed = event.level === 'fatal';
    let errored = false;
    const exceptions = event.exception && event.exception.values;

    if (exceptions) {
      errored = true;

      for (const ex of exceptions) {
        const mechanism = ex.mechanism;

        if (mechanism && mechanism.handled === false) {
          crashed = true;
          break;
        }
      }
    } // A session is updated and that session update is sent in only one of the two following scenarios:
    // 1. Session with non terminal status and 0 errors + an error occurred -> Will set error count to 1 and send update
    // 2. Session with non terminal status and 1 error + a crash occurred -> Will set status crashed and send update


    const sessionNonTerminal = session.status === 'ok';
    const shouldUpdateAndSend = sessionNonTerminal && session.errors === 0 || sessionNonTerminal && crashed;

    if (shouldUpdateAndSend) {
      (0, _session.updateSession)(session, _objectSpread(_objectSpread({}, crashed && {
        status: 'crashed'
      }), {}, {
        errors: session.errors || Number(errored || crashed)
      }));
      this.captureSession(session);
    }
  }
  /**
   * Determine if the client is finished processing. Returns a promise because it will wait `timeout` ms before saying
   * "no" (resolving to `false`) in order to give the client a chance to potentially finish first.
   *
   * @param timeout The time, in ms, after which to resolve to `false` if the client is still busy. Passing `0` (or not
   * passing anything) will make the promise wait as long as it takes for processing to finish before resolving to
   * `true`.
   * @returns A promise which will resolve to `true` if processing is already done or finishes before the timeout, and
   * `false` otherwise
   */


  _isClientDoneProcessing(timeout) {
    return new _syncpromise.SyncPromise(resolve => {
      let ticked = 0;
      const tick = 1;
      const interval = setInterval(() => {
        if (this._numProcessing == 0) {
          clearInterval(interval);
          resolve(true);
        } else {
          ticked += tick;

          if (timeout && ticked >= timeout) {
            clearInterval(interval);
            resolve(false);
          }
        }
      }, tick);
    });
  }
  /** Determines whether this SDK is enabled and a transport is present. */


  _isEnabled() {
    return this.getOptions().enabled !== false && this._transport !== undefined;
  }
  /**
   * Adds common information to events.
   *
   * The information includes release and environment from `options`,
   * breadcrumbs and context (extra, tags and user) from the scope.
   *
   * Information that is already present in the event is never overwritten. For
   * nested objects, such as the context, keys are merged.
   *
   * @param event The original event.
   * @param hint May contain additional information about the original exception.
   * @param currentScope A scope containing event metadata.
   * @returns A new event with more information.
   */


  _prepareEvent(event, hint, currentScope = (0, _currentScopes.getCurrentScope)(), isolationScope = (0, _currentScopes.getIsolationScope)()) {
    const options = this.getOptions();
    const integrations = Object.keys(this._integrations);

    if (!hint.integrations && integrations.length > 0) {
      hint.integrations = integrations;
    }

    this.emit('preprocessEvent', event, hint);

    if (!event.type) {
      isolationScope.setLastEventId(event.event_id || hint.event_id);
    }

    return (0, _prepareEvent.prepareEvent)(options, event, hint, currentScope, this, isolationScope).then(evt => {
      if (evt === null) {
        return evt;
      }

      evt.contexts = _objectSpread({
        trace: (0, _currentScopes.getTraceContextFromScope)(currentScope)
      }, evt.contexts);
      const dynamicSamplingContext = (0, _dynamicSamplingContext.getDynamicSamplingContextFromScope)(this, currentScope);
      evt.sdkProcessingMetadata = _objectSpread({
        dynamicSamplingContext
      }, evt.sdkProcessingMetadata);
      return evt;
    });
  }
  /**
   * Processes the event and logs an error in case of rejection
   * @param event
   * @param hint
   * @param scope
   */


  _captureEvent(event, hint = {}, scope) {
    return this._processEvent(event, hint, scope).then(finalEvent => {
      return finalEvent.event_id;
    }, reason => {
      if (_debugBuild.DEBUG_BUILD) {
        // If something's gone wrong, log the error as a warning. If it's just us having used a `SentryError` for
        // control flow, log just the message (no stack) as a log-level log.
        if (reason instanceof _error.SentryError && reason.logLevel === 'log') {
          _logger.logger.log(reason.message);
        } else {
          _logger.logger.warn(reason);
        }
      }

      return undefined;
    });
  }
  /**
   * Processes an event (either error or message) and sends it to Sentry.
   *
   * This also adds breadcrumbs and context information to the event. However,
   * platform specific meta data (such as the User's IP address) must be added
   * by the SDK implementor.
   *
   *
   * @param event The event to send to Sentry.
   * @param hint May contain additional information about the original exception.
   * @param currentScope A scope containing event metadata.
   * @returns A SyncPromise that resolves with the event or rejects in case event was/will not be send.
   */


  _processEvent(event, hint, currentScope) {
    const options = this.getOptions();
    const sampleRate = options.sampleRate;
    const isTransaction = isTransactionEvent(event);
    const isError = isErrorEvent(event);
    const eventType = event.type || 'error';
    const beforeSendLabel = `before send for type \`${eventType}\``; // 1.0 === 100% events are sent
    // 0.0 === 0% events are sent
    // Sampling for transaction happens somewhere else

    const parsedSampleRate = typeof sampleRate === 'undefined' ? undefined : (0, _parseSampleRate.parseSampleRate)(sampleRate);

    if (isError && typeof parsedSampleRate === 'number' && Math.random() > parsedSampleRate) {
      this.recordDroppedEvent('sample_rate', 'error', event);
      return (0, _syncpromise.rejectedSyncPromise)(new _error.SentryError(`Discarding event because it's not included in the random sample (sampling rate = ${sampleRate})`, 'log'));
    }

    const dataCategory = eventType === 'replay_event' ? 'replay' : eventType;
    const sdkProcessingMetadata = event.sdkProcessingMetadata || {};
    const capturedSpanIsolationScope = sdkProcessingMetadata.capturedSpanIsolationScope;
    return this._prepareEvent(event, hint, currentScope, capturedSpanIsolationScope).then(prepared => {
      if (prepared === null) {
        this.recordDroppedEvent('event_processor', dataCategory, event);
        throw new _error.SentryError('An event processor returned `null`, will not send event.', 'log');
      }

      const isInternalException = hint.data && hint.data.__sentry__ === true;

      if (isInternalException) {
        return prepared;
      }

      const result = processBeforeSend(this, options, prepared, hint);
      return _validateBeforeSendResult(result, beforeSendLabel);
    }).then(processedEvent => {
      if (processedEvent === null) {
        this.recordDroppedEvent('before_send', dataCategory, event);

        if (isTransaction) {
          const spans = event.spans || []; // the transaction itself counts as one span, plus all the child spans that are added

          const spanCount = 1 + spans.length;
          this.recordDroppedEvent('before_send', 'span', spanCount);
        }

        throw new _error.SentryError(`${beforeSendLabel} returned \`null\`, will not send event.`, 'log');
      }

      const session = currentScope && currentScope.getSession();

      if (!isTransaction && session) {
        this._updateSessionFromEvent(session, processedEvent);
      }

      if (isTransaction) {
        const spanCountBefore = processedEvent.sdkProcessingMetadata && processedEvent.sdkProcessingMetadata.spanCountBeforeProcessing || 0;
        const spanCountAfter = processedEvent.spans ? processedEvent.spans.length : 0;
        const droppedSpanCount = spanCountBefore - spanCountAfter;

        if (droppedSpanCount > 0) {
          this.recordDroppedEvent('before_send', 'span', droppedSpanCount);
        }
      } // None of the Sentry built event processor will update transaction name,
      // so if the transaction name has been changed by an event processor, we know
      // it has to come from custom event processor added by a user


      const transactionInfo = processedEvent.transaction_info;

      if (isTransaction && transactionInfo && processedEvent.transaction !== event.transaction) {
        const source = 'custom';
        processedEvent.transaction_info = _objectSpread(_objectSpread({}, transactionInfo), {}, {
          source
        });
      }

      this.sendEvent(processedEvent, hint);
      return processedEvent;
    }).then(null, reason => {
      if (reason instanceof _error.SentryError) {
        throw reason;
      }

      this.captureException(reason, {
        data: {
          __sentry__: true
        },
        originalException: reason
      });
      throw new _error.SentryError(`Event processing pipeline threw an error, original event will not be sent. Details have been sent as a new event.\nReason: ${reason}`);
    });
  }
  /**
   * Occupies the client with processing and event
   */


  _process(promise) {
    this._numProcessing++;
    void promise.then(value => {
      this._numProcessing--;
      return value;
    }, reason => {
      this._numProcessing--;
      return reason;
    });
  }
  /**
   * Clears outcomes on this client and returns them.
   */


  _clearOutcomes() {
    const outcomes = this._outcomes;
    this._outcomes = {};
    return Object.entries(outcomes).map(([key, quantity]) => {
      const _key$split = key.split(':'),
            _key$split2 = _slicedToArray(_key$split, 2),
            reason = _key$split2[0],
            category = _key$split2[1];

      return {
        reason,
        category,
        quantity
      };
    });
  }
  /**
   * Sends client reports as an envelope.
   */


  _flushOutcomes() {
    _debugBuild.DEBUG_BUILD && _logger.logger.log('Flushing outcomes...');

    const outcomes = this._clearOutcomes();

    if (outcomes.length === 0) {
      _debugBuild.DEBUG_BUILD && _logger.logger.log('No outcomes to send');
      return;
    } // This is really the only place where we want to check for a DSN and only send outcomes then


    if (!this._dsn) {
      _debugBuild.DEBUG_BUILD && _logger.logger.log('No dsn provided, will not send outcomes');
      return;
    }

    _debugBuild.DEBUG_BUILD && _logger.logger.log('Sending outcomes:', outcomes);
    const envelope = (0, _clientreport.createClientReportEnvelope)(outcomes, this._options.tunnel && (0, _dsn.dsnToString)(this._dsn)); // sendEnvelope should not throw
    // eslint-disable-next-line @typescript-eslint/no-floating-promises

    this.sendEnvelope(envelope);
  }
  /**
   * @inheritDoc
   */


}
/**
 * Verifies that return value of configured `beforeSend` or `beforeSendTransaction` is of expected type, and returns the value if so.
 */


exports.BaseClient = BaseClient;

function _validateBeforeSendResult(beforeSendResult, beforeSendLabel) {
  const invalidValueError = `${beforeSendLabel} must return \`null\` or a valid event.`;

  if ((0, _is.isThenable)(beforeSendResult)) {
    return beforeSendResult.then(event => {
      if (!(0, _is.isPlainObject)(event) && event !== null) {
        throw new _error.SentryError(invalidValueError);
      }

      return event;
    }, e => {
      throw new _error.SentryError(`${beforeSendLabel} rejected with ${e}`);
    });
  } else if (!(0, _is.isPlainObject)(beforeSendResult) && beforeSendResult !== null) {
    throw new _error.SentryError(invalidValueError);
  }

  return beforeSendResult;
}
/**
 * Process the matching `beforeSendXXX` callback.
 */


function processBeforeSend(client, options, event, hint) {
  const beforeSend = options.beforeSend,
        beforeSendTransaction = options.beforeSendTransaction,
        beforeSendSpan = options.beforeSendSpan;

  if (isErrorEvent(event) && beforeSend) {
    return beforeSend(event, hint);
  }

  if (isTransactionEvent(event)) {
    if (event.spans && beforeSendSpan) {
      const processedSpans = [];

      for (const span of event.spans) {
        const processedSpan = beforeSendSpan(span);

        if (processedSpan) {
          processedSpans.push(processedSpan);
        } else {
          (0, _spanUtils.showSpanDropWarning)();
          client.recordDroppedEvent('before_send', 'span');
        }
      }

      event.spans = processedSpans;
    }

    if (beforeSendTransaction) {
      if (event.spans) {
        // We store the # of spans before processing in SDK metadata,
        // so we can compare it afterwards to determine how many spans were dropped
        const spanCountBefore = event.spans.length;
        event.sdkProcessingMetadata = _objectSpread(_objectSpread({}, event.sdkProcessingMetadata), {}, {
          spanCountBeforeProcessing: spanCountBefore
        });
      }

      return beforeSendTransaction(event, hint);
    }
  }

  return event;
}

function isErrorEvent(event) {
  return event.type === undefined;
}

function isTransactionEvent(event) {
  return event.type === 'transaction';
}